import { memo, useMemo } from "react";
import { Box } from "@mui/material";
import ReactMarkdown from "react-markdown";
import { marked } from "marked";
import type { Components } from "react-markdown";

function parseMarkdownIntoBlocks(markdown: string): string[] {
  const tokens = marked.lexer(markdown);
  return tokens.map((token) => token.raw);
}

const markdownComponents: Components = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

const MemoizedMarkdownBlock = memo(
  ({ content }: { content: string }) => (
    <ReactMarkdown components={markdownComponents}>{content}</ReactMarkdown>
  ),
  (prev, next) => prev.content === next.content
);

MemoizedMarkdownBlock.displayName = "MemoizedMarkdownBlock";

const MemoizedMarkdown = memo(({ content, id }: { content: string; id: string }) => {
  const blocks = useMemo(
    () => (content ? parseMarkdownIntoBlocks(content) : []),
    [content]
  );

  return (
    <Box
      sx={{
        fontSize: 14,
        lineHeight: "20px",
        color: "text.primary",
        "& p": { m: 0 },
        "& p + p": { mt: 1 },
        "& ul, & ol": { pl: 2.5, my: 0.5 },
        "& li": { mb: 0.25 },
        "& strong": { fontWeight: 700 },
        "& code": {
          fontFamily: "monospace",
          fontSize: 13,
          bgcolor: "rgba(0,0,0,0.06)",
          px: 0.5,
          borderRadius: "3px",
        },
        "& pre": {
          bgcolor: "rgba(0,0,0,0.06)",
          p: 1,
          borderRadius: "6px",
          overflowX: "auto",
          "& code": { bgcolor: "transparent", px: 0 },
        },
        "& h1, & h2, & h3": { fontSize: 14, fontWeight: 700, my: 0.5 },
        "& a": { color: "inherit" },
        "& blockquote": {
          borderLeft: "3px solid",
          borderColor: "divider",
          pl: 1,
          ml: 0,
          my: 0.5,
        },
      }}
    >
      {blocks.map((block, index) => (
        <MemoizedMarkdownBlock key={`${id}-block_${index}`} content={block} />
      ))}
    </Box>
  );
});

MemoizedMarkdown.displayName = "MemoizedMarkdown";

export default MemoizedMarkdown;
